/**
 * Phase A.3 — `Intl.*` formatters wrapper.
 *
 * Centralizes every date / number / currency / relative-time / list
 * render in LexIDE behind one tiny module. Two surfaces:
 *
 *   - Pure functions  — `formatDate(value, locale, opts?)` etc. Use
 *     these from any non-React context (utility modules, tests,
 *     server-side render in error catalogs, scripts).
 *   - React hook       — `useFormatters()` returns pre-bound versions
 *     of all five, with `locale` read from `useLanguageStore`.
 *     Components re-render on locale change via Zustand's
 *     subscription (no manual wiring required).
 *
 * **No vitest hook-render test.** lexide's vitest config is
 * `environment: "node"` by design (rendering tests → Playwright).
 * The hook is a thin selector + closure over the pure functions;
 * Zustand's reactivity is exercised end-to-end by the Phase A.3
 * Playwright pilot spec.
 *
 * **Invalid input policy.** Only the Date-accepting formatters
 * (`formatDate`, `formatRelativeTime`) have an "invalid input"
 * branch: when the supplied value doesn't parse
 * (`Number.isNaN(date.getTime())`), they return the input string
 * as-is — matches the existing convention in legacy sites being
 * migrated (DeadlinesView, TimelineView, etc.), so the migration
 * is a behavior-preserving rename. The number / currency / list
 * formatters take typed inputs (`number` / `string[]`) and inherit
 * `Intl.*`'s native handling of edge cases (e.g. `NaN` → `"NaN"`).
 */

import { useLanguageStore } from "@/stores/useLanguageStore";
import type { LanguagePreference } from "@/lib/language";

// ── Pure functions ─────────────────────────────────────────────────

/**
 * Parse a Date | string input into a Date, or return null when the
 * input is unparseable. Internal helper.
 */
function asDate(value: Date | string): Date | null {
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Format a Date or ISO-string for display in the user's locale.
 * Default options: `{ day: "2-digit", month: "long", year: "numeric" }`
 * (matches the majority of migrated sites). Pass explicit options to
 * override.
 */
export function formatDate(
  value: Date | string,
  locale: LanguagePreference,
  options: Intl.DateTimeFormatOptions = {
    day: "2-digit",
    month: "long",
    year: "numeric",
  },
): string {
  const d = asDate(value);
  if (!d) return typeof value === "string" ? value : String(value);
  return new Intl.DateTimeFormat(locale, options).format(d);
}

/**
 * Format a number with the user's locale grouping + decimal
 * separator. Pass `{ style: "percent" }` etc via options.
 */
export function formatNumber(
  value: number,
  locale: LanguagePreference,
  options?: Intl.NumberFormatOptions,
): string {
  return new Intl.NumberFormat(locale, options).format(value);
}

/**
 * Format an amount as currency. Currency CODE is org-scoped
 * (`Organization.currency`); display locale is user-scoped.
 *
 * Example:
 *   formatCurrency(1234.56, "BRL", "pt-BR")  → "R$ 1.234,56"
 *   formatCurrency(1234.56, "BRL", "en-US")  → "R$ 1,234.56" (engine-dependent spacing)
 *   formatCurrency(1234.56, "USD", "pt-BR")  → "US$ 1.234,56"
 *   formatCurrency(1234.56, "USD", "en-US")  → "$1,234.56"
 */
export function formatCurrency(
  amount: number,
  currencyCode: string,
  locale: LanguagePreference,
  options?: Intl.NumberFormatOptions,
): string {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currencyCode,
    ...options,
  }).format(amount);
}

/**
 * Relative-time formatter ("3 days ago" / "há 3 dias" / "hace 3 días").
 * Picks the largest natural unit (year/month/day/hour/minute/second)
 * that yields a magnitude ≥ 1. Sub-second / sub-minute inputs round
 * to the closest minute (matches the existing TimelineEntryRow
 * convention where < 60s → "agora").
 *
 * `toIso` defaults to "now".
 */
export function formatRelativeTime(
  fromIso: Date | string,
  toIso: Date | string | undefined,
  locale: LanguagePreference,
  /** `"narrow"` gives the compact form (EN "22m ago" · PT "há 22 min"); default
   *  `"long"` gives "22 minutes ago". */
  style: Intl.RelativeTimeFormatStyle = "long",
): string {
  const from = asDate(fromIso);
  if (!from) return typeof fromIso === "string" ? fromIso : String(fromIso);
  // If `toIso` is supplied but unparseable, fall back to "now" rather
  // than echoing the broken value — the caller asked to format `fromIso`,
  // not surface their bad `toIso`. Matches the omitted-toIso semantics.
  const to = toIso === undefined ? new Date() : (asDate(toIso) ?? new Date());

  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto", style });
  const diffSec = Math.round((from.getTime() - to.getTime()) / 1000);
  const abs = Math.abs(diffSec);

  // Pick the largest unit that yields a magnitude ≥ 1 step. Order
  // matters — we want "1 year" for 365 days, not "365 days".
  const UNITS: Array<{ unit: Intl.RelativeTimeFormatUnit; seconds: number }> = [
    { unit: "year",   seconds: 365 * 24 * 60 * 60 },
    { unit: "month",  seconds: 30 * 24 * 60 * 60 },
    { unit: "week",   seconds: 7 * 24 * 60 * 60 },
    { unit: "day",    seconds: 24 * 60 * 60 },
    { unit: "hour",   seconds: 60 * 60 },
    { unit: "minute", seconds: 60 },
  ];

  for (const { unit, seconds } of UNITS) {
    if (abs >= seconds) {
      return rtf.format(Math.round(diffSec / seconds), unit);
    }
  }
  // < 60 seconds: "now" via numeric: "auto" produces locale-correct copy.
  return rtf.format(0, "second");
}

/**
 * List formatter ("a, b, e c" / "a, b, and c"). Use options to
 * select `style: "long" | "short" | "narrow"` and
 * `type: "conjunction" | "disjunction" | "unit"`.
 */
export function formatList(
  items: string[],
  locale: LanguagePreference,
  options?: Intl.ListFormatOptions,
): string {
  return new Intl.ListFormat(locale, options).format(items);
}

// ── React hook ─────────────────────────────────────────────────────

export interface BoundFormatters {
  date: (value: Date | string, options?: Intl.DateTimeFormatOptions) => string;
  number: (value: number, options?: Intl.NumberFormatOptions) => string;
  currency: (
    amount: number,
    currencyCode: string,
    options?: Intl.NumberFormatOptions,
  ) => string;
  relativeTime: (
    fromIso: Date | string,
    toIso?: Date | string,
    style?: Intl.RelativeTimeFormatStyle,
  ) => string;
  list: (items: string[], options?: Intl.ListFormatOptions) => string;
  /** The locale these formatters are bound to. Exposed so components
   *  can pass it through to non-React utility calls when needed. */
  locale: LanguagePreference;
}

/**
 * React hook: pre-bound formatters using the active locale from
 * `useLanguageStore`. Components re-render automatically on locale
 * change because Zustand's selector subscribes to `state.lang`.
 *
 * Usage:
 *   const fmt = useFormatters();
 *   return <span>{fmt.date(dueDate)}</span>;
 */
export function useFormatters(): BoundFormatters {
  const locale = useLanguageStore((s) => s.lang);
  return {
    date: (value, options) => formatDate(value, locale, options),
    number: (value, options) => formatNumber(value, locale, options),
    currency: (amount, currencyCode, options) =>
      formatCurrency(amount, currencyCode, locale, options),
    relativeTime: (fromIso, toIso, style) =>
      formatRelativeTime(fromIso, toIso, locale, style),
    list: (items, options) => formatList(items, locale, options),
    locale,
  };
}
